import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3, Users, FileText, Building2, Edit3, Calendar,
  CreditCard, Bell, Settings,
  TrendingUp, Send,
  Shield, RefreshCw, User, Activity, Zap,
  MessageSquare, ClipboardCheck
} from 'lucide-react';
import PortalLayout from './PortalLayout';
import MembersPage from '../admin/MembersPage';
import DocumentManagement from '../admin/DocumentManagement';
import ContentManager from '../admin/ContentManager';
import ServiceProviderManagement from '../admin/ServiceProviderManagement';
import EventManagement from '../admin/EventManagement';
import ActivityLogPanel from '../admin/activity/ActivityLogPanel';
import InsightDigest from '../admin/InsightDigest';
import AccDashboardTile from '../admin/AccDashboardTile';
import MessageCenter from '../admin/MessageCenter';
import ACCReview from '../common/ACCReview';
import { canSeeACCReview } from '../../lib/accVisibility';
import { supabase } from '../../lib/supabase';
import { withSupabaseQueryTimeout } from '../../lib/supabaseTimeout';
import { useHOA } from '../../contexts/HOAContext';
import { hoaProfile, hoaSettingsRows } from '../../lib/hoaProfile';

import { useAuth } from '../../contexts/AuthContext';
import { Card, SectionHeader } from '../ui';
import { activityService, ACTION_TYPES } from '../../services/activityService';
import BalanceImportPanel from '../admin/BalanceImportPanel';
import { AssociationsPanel } from '../admin/associations/AssociationsPanel';

const AdminPortal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  const { tabVisibility, user, isPlatformOwner } = useAuth();
  const [notifications, setNotifications] = useState({});
  const notificationFetchInFlightRef = React.useRef(false);

  React.useEffect(() => {
    if (!user) return;
    const fetchNotifications = async () => {
      if (notificationFetchInFlightRef.current) return;
      notificationFetchInFlightRef.current = true;
      try {
        const { count } = await withSupabaseQueryTimeout(
          () => supabase
            .from('notifications')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('is_read', false)
            .eq('type', 'message'),
          'Loading admin message notification count',
          30000
        );
        setNotifications({ messages: count || 0 });
      } catch (error) {
        console.warn('Admin notification refresh failed:', error?.message || error);
      } finally {
        notificationFetchInFlightRef.current = false;
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    window.addEventListener('notifications_read', fetchNotifications);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications_read', fetchNotifications);
    };
  }, [user]);

  const allTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    ...(isPlatformOwner ? [{ id: 'associations', label: 'Associations', icon: Shield }] : []),
    { id: 'activity', label: 'Activity Log', icon: Activity },
    { id: 'insight-digest', label: 'Insight Digest', icon: TrendingUp, adminOnly: true },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'messages', label: 'Inbox', icon: MessageSquare },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'providers', label: 'Service Providers', icon: Building2 },
    { id: 'content', label: 'Content', icon: Edit3 },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    ...(canSeeACCReview(user) ? [{ id: 'accReview', label: 'ACC Review', icon: ClipboardCheck }] : []),
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'profile', label: 'My Profile', icon: User },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const tabs = allTabs.filter(tab => {
    if (tab.id === 'settings' || tab.id === 'associations') return true; // never hidden by tab visibility
    const vis = tabVisibility?.admin;
    return !vis || vis[tab.id] === undefined || vis[tab.id];
  });

  const content = {
    dashboard: <AdminDashboard onNavigateToTab={(t) => setSearchParams({ tab: t })} currentUser={user} />,
    associations: isPlatformOwner ? <AssociationsPanel /> : null,
    activity: <div className="space-y-6"><ActivityLogPanel /></div>,
    'insight-digest': <InsightDigest />,
    users: <MembersPage />,
    documents: <DocumentManagement />,
    messages: <MessageCenter />,
    events: <EventManagement />,
    providers: <ServiceProviderManagement />,
    content: <ContentManager />,
    payments: <PaymentManagement />,
    accReview: <ACCReview />,
    notifications: <NotificationCenter />,
    profile: <AdminProfile />,
    settings: <SystemSettings />,
  };

  return (
    <PortalLayout title="Admin Portal" tabs={tabs} activeTab={activeTab} onTabChange={(t) => setSearchParams({ tab: t })} userRole="admin" notifications={notifications}>
      {content[activeTab]}
    </PortalLayout>
  );
};

/* ── RAG BUDGET CARD ──────────────────────────────────── */
const HARD_LIMIT = 10.00;
const WARNING_THRESHOLD = 8.00;

const RagBudgetCard = () => {
  const { hoa } = useHOA();
  const [budgetData, setBudgetData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const loadBudget = React.useCallback(async () => {
    if (!hoa?.id) return;
    const now = new Date();
    const { data } = await supabase
      .from('rag_budget')
      .select('estimated_cost_usd, total_tokens_used, hard_limit_usd, last_query_at')
      .eq('hoa_id', hoa.id)
      .eq('month', now.getMonth() + 1)
      .eq('year', now.getFullYear())
      .maybeSingle();

    const spent = data?.estimated_cost_usd || 0;
    const limit = data?.hard_limit_usd || HARD_LIMIT;
    const percentUsed = Math.round((spent / limit) * 100);
    const COST_PER_QUERY = 0.002;
    const estimatedQueriesRemaining = Math.floor(Math.max(0, limit - spent) / COST_PER_QUERY);

    setBudgetData({
      spent,
      limit,
      percentUsed,
      estimatedQueriesRemaining,
      total_tokens_used: data?.total_tokens_used || 0,
      last_query_at: data?.last_query_at || null,
      isWarning: spent >= WARNING_THRESHOLD,
      isCapped: spent >= limit,
      month: now.getMonth(),
      year: now.getFullYear(),
    });
    setLoading(false);
  }, [hoa?.id]);

  React.useEffect(() => {
    loadBudget();
    const interval = setInterval(loadBudget, 60000);
    return () => clearInterval(interval);
  }, [loadBudget]);

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextResetDate = nextMonth.toLocaleDateString();

  const barColor = !budgetData ? 'bg-slate-300'
    : budgetData.percentUsed >= 100 ? 'bg-red-500'
    : budgetData.percentUsed >= 80 ? 'bg-amber-500'
    : 'bg-secondary';

  return (
    <Card>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-secondary/10">
            <Zap className="w-5 h-5 text-secondary" />
          </div>
          <h3 className="font-display font-bold text-slate-900 text-lg">AI Search Budget</h3>
        </div>
        <button onClick={loadBudget} className="p-1.5 text-slate-400 hover:text-secondary hover:bg-section-bg rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-forest-700" />
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="text-sm text-slate-600 mb-2 font-medium">{monthNames[budgetData.month]} {budgetData.year}</p>

            <div className="w-full bg-slate-200 rounded-full h-3 mb-2 overflow-hidden">
              <div className={`${barColor} h-3 rounded-full transition-all duration-500`} style={{ width: `${Math.min(budgetData.percentUsed, 100)}%` }} />
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-1 text-sm">
              <span className="font-semibold text-slate-800">
                ${budgetData.spent.toFixed(4)} of ${budgetData.limit.toFixed(2)} used
              </span>
              <span className="text-slate-500">~{budgetData.estimatedQueriesRemaining} queries remaining</span>
            </div>
          </div>

          {budgetData.isWarning && !budgetData.isCapped && (
            <div className="p-3 bg-amber-50 text-amber-800 text-sm rounded-lg font-medium mb-3 border border-amber-200">
              Approaching monthly limit
            </div>
          )}

          {budgetData.isCapped && (
            <div className="p-3 bg-red-50 text-red-800 text-sm rounded-lg font-medium mb-3 border border-red-200">
              Monthly limit reached — AI search paused until {monthNames[(budgetData.month + 1) % 12]} 1st
            </div>
          )}

          {budgetData.last_query_at && (
            <p className="text-xs text-slate-400 mb-3">
              Last query: {new Date(budgetData.last_query_at).toLocaleDateString()} at {new Date(budgetData.last_query_at).toLocaleTimeString()}
            </p>
          )}

          <div className="flex justify-between items-center text-xs mt-4 pt-4 border-t border-amber-100/30">
            <span className="text-slate-500">Resets {nextResetDate}</span>
            <span className="text-slate-400">{budgetData.total_tokens_used.toLocaleString()} tokens used</span>
          </div>
        </>
      )}
    </Card>
  );
};

const AdminDashboard = ({ onNavigateToTab, currentUser }) => {
  const { hoa } = useHOA();
  const [memberCount, setMemberCount] = React.useState('—');
  const [nextMeeting, setNextMeeting] = React.useState('Not scheduled');

  React.useEffect(() => {
    if (!hoa?.id) return;
    const loadDashboardStats = async () => {
      try {
        const { count: mc } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .eq('hoa_id', hoa.id)
          .eq('is_active', true);
        setMemberCount(mc ?? '—');

        const { data: mtg } = await supabase
          .from('board_meetings')
          .select('scheduled_at')
          .eq('hoa_id', hoa.id)
          .gte('scheduled_at', new Date().toISOString())
          .order('scheduled_at', { ascending: true })
          .limit(1)
          .single();
        if (mtg?.scheduled_at) {
          const d = new Date(mtg.scheduled_at);
          setNextMeeting(d.toLocaleDateString('en-US', {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit'
          }));
        }
      } catch (e) {}
    };
    loadDashboardStats();
  }, [hoa?.id]);

  // Association-specific "attention" rows (e.g. Crestview's Legal Billing) come
  // from hoas.settings.status_attention; the platform rows are the same everywhere.
  const profile = hoaProfile(hoa);
  const ATTENTION_ICONS = { 'Legal Billing': 'gavel', 'Member Roster': 'group' };
  const systemStatus = [
    { label: 'Authentication',   status: 'operational', icon: 'lock'           },
    { label: 'Document Storage', status: 'operational', icon: 'folder'         },
    ...profile.statusAttention.map((label) => ({ label, status: 'attention', icon: ATTENTION_ICONS[label] || 'flag' })),
    { label: 'Backend / DB',     status: 'operational', icon: 'storage'        },
    { label: 'AI Search',        status: 'operational', icon: 'auto_awesome'   },
  ];

  const statusStyle = {
    operational: { dot: 'bg-emerald-400', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-100' },
    attention:   { dot: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50 border-amber-100'     },
    down:        { dot: 'bg-red-400',     text: 'text-red-700',     bg: 'bg-red-50 border-red-100'         },
  };

  return (
    <div className="space-y-8">

      {/* ── HERO ── */}
      <section className="relative h-auto min-h-[260px] py-8 md:py-0 md:h-[260px] rounded-3xl overflow-hidden warm-tactile-shadow">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/hero-bg.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-transparent" />
        <div className="relative h-full flex flex-col justify-center px-6 md:px-14 max-w-2xl">
          <p className="text-white/60 text-xs font-bold uppercase tracking-widest mb-3">
            {hoaProfile(hoa).shortName} · Platform Administration
          </p>
          <h2 className="text-4xl font-extrabold text-white tracking-tight mb-3">
            Admin Dashboard
          </h2>
          <p className="text-white/70 text-sm font-medium mb-8 leading-relaxed">
            {profile.currentFiscalYear ? `Fiscal year ${profile.currentFiscalYear} · ` : ''}{memberCount} active members
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4 mt-auto sm:mt-0">
            <button
              onClick={() => onNavigateToTab('users')}
              className="bg-secondary text-white px-7 py-3 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all shadow-lg w-full sm:w-auto"
            >
              Manage Users
            </button>
            <button
              onClick={() => onNavigateToTab('insight-digest')}
              className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-7 py-3 rounded-full font-bold text-sm hover:bg-white/20 transition-all w-full sm:w-auto"
            >
              Insight Digest
            </button>
          </div>
        </div>
      </section>

      {/* ── STAT ROW ── */}
      <section className="bg-section-bg p-6 rounded-[2.5rem]">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">

          <div className="min-h-[48px] py-4 pr-6 border-r border-white/60 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
              <span className="material-symbols-outlined text-[20px] text-secondary">home_work</span>
            </div>
            <p className="text-3xl font-extrabold text-primary-dark leading-none">{profile.totalUnitsLabel || '—'}</p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Total Units</p>
          </div>

          <div className="min-h-[48px] py-4 px-6 border-r border-white/60 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
              <span className="material-symbols-outlined text-[20px] text-secondary">savings</span>
            </div>
            <p className="text-3xl font-extrabold text-secondary leading-none">{profile.reserveShortLabel || '—'}</p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Reserve Balance</p>
          </div>

          <div className="min-h-[48px] py-4 px-6 border-r border-white/60 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
              <span className="material-symbols-outlined text-[20px] text-secondary">calendar_today</span>
            </div>
            <p className="text-xl font-extrabold text-primary-dark leading-none">{nextMeeting}</p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Next Meeting</p>
          </div>

          <div className="min-h-[48px] py-4 pl-6 flex flex-col items-center text-center">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-4 warm-tactile-shadow">
              <span className="material-symbols-outlined text-[20px] text-amber-500">warning</span>
            </div>
            <p className="text-3xl font-extrabold text-amber-500 leading-none">{profile.priorityItems.length}</p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mt-1">Open Issues</p>
          </div>

        </div>
      </section>

      {/* ── MAIN GRID ── */}
      <section className="bg-section-bg p-6 rounded-[2.5rem] space-y-6">

        {/* Row 1 — System Status + Quick Nav */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* System Status — 2 cols wide */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-8 warm-tactile-shadow border border-slate-200/50">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                <span className="material-symbols-outlined text-2xl text-emerald-600">monitor_heart</span>
              </div>
              <div>
                <h5 className="text-xl font-bold text-primary-dark">System Status</h5>
                <p className="text-xs font-medium text-slate-400">Platform health overview</p>
              </div>
              <span className="ml-auto bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full border border-emerald-100">
                {systemStatus.filter(s => s.status === 'operational').length}/{systemStatus.length} Operational
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {systemStatus.map(item => {
                const s = statusStyle[item.status];
                return (
                  <div key={item.label} className={`flex items-center gap-3 p-4 rounded-xl border ${s.bg}`}>
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${s.dot}`} />
                    <span className="material-symbols-outlined text-[18px] text-slate-400">{item.icon}</span>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-primary-dark truncate">{item.label}</p>
                      <p className={`text-[10px] font-bold uppercase tracking-wider ${s.text}`}>
                        {item.status}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Admin Quick Nav */}
          <div className="bg-[#DFE7D8] rounded-2xl p-8 warm-tactile-shadow border border-emerald-300/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-white/90 rounded-xl flex items-center justify-center border border-white shadow-sm">
                <span className="material-symbols-outlined text-2xl text-emerald-900">admin_panel_settings</span>
              </div>
              <h5 className="text-xl font-bold text-emerald-950">Admin Tools</h5>
            </div>
            <div className="space-y-2.5">
              {[
                { label: 'Users',          icon: 'manage_accounts', tab: 'users'          },
                { label: 'Documents',      icon: 'description',     tab: 'documents'      },
                { label: 'Activity Log',   icon: 'pulse',           tab: 'activity'       },
                { label: 'Insight Digest', icon: 'insights',        tab: 'insight-digest' },
                { label: 'Notifications',  icon: 'notifications',   tab: 'notifications'  },
                { label: 'Settings',       icon: 'settings',        tab: 'settings'       },
              ].map(item => (
                <button
                  key={item.tab}
                  onClick={() => onNavigateToTab(item.tab)}
                  className="w-full flex items-center justify-between p-3 bg-white/70 hover:bg-white rounded-xl border border-white transition-all group/btn"
                >
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-[18px] text-emerald-800">{item.icon}</span>
                    <span className="text-sm font-bold text-emerald-950">{item.label}</span>
                  </div>
                  <span className="material-symbols-outlined text-sm text-emerald-600 transition-transform group-hover/btn:translate-x-1">chevron_right</span>
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Row 2 — RAG Budget Card (existing component, unstyled wrapper) */}
        <div className="bg-white rounded-2xl p-2 warm-tactile-shadow border border-slate-200/50 overflow-hidden">
          <RagBudgetCard />
        </div>

        {/* Row 3 — ACC Tile (conditional, existing component) */}
        {currentUser?.is_acc_member && (
          <div className="bg-[#1D2B20] rounded-2xl p-2 warm-tactile-shadow overflow-hidden relative group">
            <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-white/5 rounded-full blur-3xl group-hover:bg-white/10 transition-colors duration-500 pointer-events-none" />
            <AccDashboardTile />
          </div>
        )}

      </section>

    </div>
  );
};

/* ── EVENT MANAGEMENT ─────────────────────────────────── */
const PaymentManagement = () => <BalanceImportPanel />;

/* NOTIFICATION CENTER */
const NotificationCenter = () => {
  const [msg, setMsg] = useState({ audience: 'all', subject: '', body: '' });

  return (
    <Card>
      <SectionHeader icon={Bell} title="Send Notification" color="text-secondary" accent="bg-secondary/10" />
      <form className="space-y-4 max-w-xl">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Audience</label>
            <select value={msg.audience} onChange={e => setMsg(m => ({ ...m, audience: e.target.value }))}
              className="w-full px-3.5 py-2.5 bg-section-bg border border-amber-100/30 rounded-xl text-slate-800 text-base md:text-sm outline-none focus:border-secondary transition-all">
              <option value="all">All Homeowners</option>
              <option value="board">Board Members</option>
              <option value="delinquent">Delinquent Accounts</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Channel</label>
            <select className="w-full px-3.5 py-2.5 bg-section-bg border border-amber-100/30 rounded-xl text-slate-800 text-base md:text-sm outline-none focus:border-secondary transition-all">
              <option>Email</option>
              <option>Portal Notification</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Subject</label>
          <input value={msg.subject} onChange={e => setMsg(m => ({ ...m, subject: e.target.value }))}
            placeholder="Notification subject…"
            className="w-full px-3.5 py-2.5 bg-section-bg border border-amber-100/30 rounded-xl text-slate-800 text-base md:text-sm outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/10 transition-all" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Message</label>
          <textarea rows={5} value={msg.body} onChange={e => setMsg(m => ({ ...m, body: e.target.value }))}
            placeholder="Notification content…"
            className="w-full px-3.5 py-2.5 bg-section-bg border border-amber-100/30 rounded-xl text-slate-800 text-base md:text-sm outline-none focus:border-secondary focus:ring-2 focus:ring-secondary/10 transition-all resize-none" />
        </div>
        <button type="button" className="flex items-center gap-2 px-5 py-2.5 bg-secondary hover:brightness-110 text-white text-sm font-semibold rounded-xl transition-colors">
          <Send className="w-4 h-4" />
          Send Notification
        </button>
      </form>
    </Card>
  );
};

/* ── SYSTEM SETTINGS ──────────────────────────────────── */
const SystemSettings = () => {
  const { tabVisibility, updateTabVisibility } = useAuth();
  const { hoa } = useHOA();

  const allTabsByTier = {
    member: [
      { id: 'dashboard', label: 'Dashboard' }, { id: 'documents', label: 'Documents' },
      { id: 'payments', label: 'Dues & Payments' }, { id: 'requests', label: 'Submit Request' },
      { id: 'events', label: 'Events' }, { id: 'directory', label: 'Directory' },
      { id: 'profile', label: 'My Profile' }, { id: 'settings', label: 'Settings' },
    ],
    board: [
      { id: 'dashboard', label: 'Dashboard' }, { id: 'documents', label: 'Documents' },
      { id: 'meetings', label: 'Meetings' }, { id: 'financials', label: 'Financials' },
      { id: 'members', label: 'Members' }, { id: 'voting', label: 'Voting' },
      { id: 'communications', label: 'Communications' }, { id: 'settings', label: 'Settings' },
    ],
    admin: [
      { id: 'dashboard', label: 'Dashboard' }, { id: 'activity', label: 'Activity Log' }, { id: 'users', label: 'Users' },
      { id: 'documents', label: 'Documents' }, { id: 'providers', label: 'Service Providers' },
      { id: 'content', label: 'Content' }, { id: 'calendar', label: 'Events' },
      { id: 'payments', label: 'Payments' }, { id: 'notifications', label: 'Notifications' },
      { id: 'settings', label: 'Settings' },
    ],
  };

  const tierLabels = { member: 'Homeowner', board: 'Board', admin: 'Admin' };

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader icon={Settings} title="System Settings" color="text-slate-700" accent="bg-slate-100" />
        <div className="space-y-3 max-w-md">
          {hoaSettingsRows(hoaProfile(hoa)).map((f, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-section-bg rounded-xl border border-amber-100/30">
              <span className="text-slate-500 text-sm">{f.label}</span>
              <span className="text-slate-900 text-sm font-medium text-right max-w-48 truncate" title={f.value}>{f.value}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <SectionHeader icon={Shield} title="Tab Visibility by Tier" color="text-amber-700" accent="bg-amber-50" />
        <p className="text-slate-500 text-sm mb-5">Control which tabs are visible for each user tier. Dashboard is always visible.</p>
        <div className="space-y-6">
          {Object.entries(allTabsByTier).map(([tier, tierTabs]) => (
            <div key={tier}>
              <h4 className="text-slate-900 font-semibold text-sm mb-3 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${tier === 'admin' ? 'bg-amber-500' : tier === 'board' ? 'bg-secondary' : 'bg-slate-400'
                  }`} />
                {tierLabels[tier]} Portal
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {tierTabs.map((tab) => {
                  const isVisible = tabVisibility?.[tier]?.[tab.id] !== false;
                  const isDashboard = tab.id === 'dashboard';
                  return (
                    <button
                      key={tab.id}
                      onClick={() => !isDashboard && updateTabVisibility(tier, tab.id, !isVisible)}
                      className={`flex items-center justify-between p-2.5 rounded-xl border text-sm transition-all ${isDashboard
                        ? 'bg-slate-50 border-amber-100/40 text-slate-400 cursor-not-allowed'
                        : isVisible
                          ? 'bg-secondary/10 border-secondary/30 text-forest-800 hover:bg-forest-100'
                          : 'bg-slate-50 border-amber-100/40 text-slate-400 hover:bg-slate-100'
                        }`}
                    >
                      <span className="font-medium text-xs">{tab.label}</span>
                      <div className={`w-8 h-4.5 rounded-full relative transition-colors ${isDashboard ? 'bg-slate-300' : isVisible ? 'bg-secondary' : 'bg-slate-200'
                        }`}>
                        <div className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow transition-transform ${isVisible ? 'translate-x-4' : 'translate-x-0.5'
                          }`} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
/* ── ADMIN PROFILE ────────────────────────────────────── */
const AdminProfile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
  });

  const handleSave = async () => {
    // Save locally to user object if we had a real backend
    // Log action
    await activityService.log(ACTION_TYPES.USER_EDITED, user, { user_name: editForm.name });
    setIsEditing(false);
  };

  const fields = [
    { label: 'Name', value: isEditing ? <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full text-right bg-transparent outline-none focus:border-b border-slate-300 text-base" /> : editForm.name || '—' },
    { label: 'Email', value: isEditing ? <input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} className="w-full text-right bg-transparent outline-none focus:border-b border-slate-300 text-base" /> : editForm.email || '—' },
    { label: 'Address', value: isEditing ? <input value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} className="w-full text-right bg-transparent outline-none focus:border-b border-slate-300 text-base" /> : editForm.address || '—' },
    { label: 'Phone', value: isEditing ? <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className="w-full text-right bg-transparent outline-none focus:border-b border-slate-300 text-base" /> : editForm.phone || '—' },
  ];
  return (
    <Card>
      <div className="flex justify-between items-center mb-5">
        <SectionHeader icon={User} title="My Profile" />
        {!isEditing ? (
          <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors">
            <Edit3 className="w-4 h-4" /> Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setIsEditing(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-slate-700 rounded-xl text-sm transition-colors">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 bg-secondary hover:brightness-110 text-white font-semibold rounded-xl text-sm transition-colors">Save</button>
          </div>
        )}
      </div>
      <div className="space-y-3 max-w-lg">
        {fields.map((f, i) => (
          <div key={i} className="flex items-center justify-between p-3.5 bg-section-bg rounded-xl border border-amber-100/30">
            <span className="text-slate-500 text-sm w-1/3 shrink-0">{f.label}</span>
            <span className="text-slate-900 text-sm font-medium w-full text-right">{f.value}</span>
          </div>
        ))}
        <div className="flex items-center justify-between p-3.5 bg-section-bg rounded-xl border border-amber-100/30">
          <span className="text-slate-500 text-sm shrink-0">Role</span>
          <span className="px-2.5 py-0.5 text-xs rounded-full border font-medium bg-amber-100 text-amber-800 border-amber-200">Administrator</span>
        </div>
      </div>
    </Card>
  );
};

export default AdminPortal;
